import { Selection } from "@tiptap/pm/state";
import { difference } from "es-toolkit";
import { autorun, computed, makeAutoObservable, reaction } from "mobx";
import getCaretCoordinates from "textarea-caret";

import { jumpToSiblingNode } from "@/components/tiptap/keyboard-handler/KeyboardHandler";
import { nodeIsExpression } from "@/components/tiptap/tiptap-expression/utils";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";

import { DEFAULT_CURSOR_CONTEXT_ID } from "./constants";
import { jumpLine } from "./cursor/jumpLine";
import { findCursorPosition } from "./cursor/moveLeftRight";
import { moveUpDown, WRAPPER_NODE_CLASS_NAME } from "./cursor/moveUpDown";
import { getNodeLevel } from "./cursor/utils";
import { FunctionNodeModel } from "./nodes/FunctionNodeModel";
import { PlaceholderNodeModel } from "./nodes/PlaceholderNodeModel";

import type { MoveToOptions } from "./cursor/moveUpDown";
import type { DocumentModel } from "@/models/DocumentModel";
import type { ClassOf } from "@/models/nodes/BaseNodeModel";
import type { NodeModel, NodeId } from "@/models/nodes/types";
import type { Node as ProsemirrorNode } from "@tiptap/pm/model";

export {
  CURSOR_POSITION_CLASS_NAME,
  CAPTURE_CURSOR_CLASS_NAME,
  WRAPPER_NODE_CLASS_NAME,
} from "./cursor/moveUpDown";

export type CursorMovementType = "visual" | "logical";

export type BlockName = "text" | "math" | "inline-math" | "image" | "separation-line" | "embed";

export class CursorModel {
  doc: DocumentModel;

  node?: InlineNodeModel = undefined;

  /**
   * When we set the combination of `node` and `index` to place cursor, we store thursor position:
   * `node.nodes[index - 1]`. If `indexNodeId` is undefined, then `index` is 0 (cursor at start). If `indexNodeId` is
   * the same as parent `node.id`, then `index` is the length of `node.nodes`.
   * We need it to avoid shifting cursor when the new element is added before the current cursor. For example,
   * for collaborative editing.
   */
  indexNodeId?: NodeId = undefined;

  /**
   * If the node undef indexNodeId is removed, lastIndex will be used instead.
   */
  private lastIndex?: number = undefined;

  private movementTypeValue: CursorMovementType = "visual";

  private disposers: Array<() => void> = [];

  /** Save cursor left coordinate to keep it when you jump between the lines. */
  private lockLeft?: number;

  private allowReleaseLockLeft = true;

  constructor(doc: DocumentModel) {
    this.doc = doc;
    makeAutoObservable(this);

    this.disposers.push(
      reaction(
        () => {
          return [this.doc.editor?.editorState.isExpression];
        },
        () => {
          if (!this.doc.editor.editorState.isExpression) {
            this.reset();
          }
        },
      ),
    );

    this.disposers.push(
      reaction(
        () => {
          return this.node;
        },
        (_node, prev) => {
          this.afterCursorMove(prev);
        },
      ),
    );

    this.disposers.push(
      autorun(() => {
        this.doc.awareness?.setCursor(this.node?.id, this.index);
      }),
    );

    this.disposers.push(
      reaction(
        () => this.doc.editor?.tiptap,
        (tiptap, prevTiptap) => {
          prevTiptap?.off("selectionUpdate", this.releaseLockLeft);
          tiptap?.on("selectionUpdate", this.releaseLockLeft);
        },
        { fireImmediately: true },
      ),
    );

    this.disposers.push(
      reaction(
        () => this.node?.parent,
        (parent) => {
          if (this.node && !parent) {
            this.recoverCursorPosition();
          }
        },
      ),
    );

    this.disposers.push(
      reaction(this.trackAncestorInInline, this.onAncestorInInlineChange, {
        equals: (a, b) => a.node === b.node && a.ancestorIndex === b.ancestorIndex,
      }),
    );
  }

  /**
   * When the cursor's InlineNode is removed from the tree, try to recover
   * by moving to the wrapper's root inline at position 0.
   */
  private recoverCursorPosition() {
    const wrapper = this.parentWrapperNode;

    if (wrapper?.inlineNode) {
      this.moveTo(wrapper.inlineNode, 0);
    } else {
      this.reset();
    }
  }

  /**
   * Tracks the index of the cursor's nearest ancestor that is a direct child of an InlineNodeModel.
   * When that index drops to -1, it means the ancestor was removed from its parent inline.
   */
  private trackAncestorInInline = (): {
    node: InlineNodeModel | undefined;
    ancestorIndex: number | undefined;
    ancestorParent: NodeModel | undefined;
  } => {
    const { node } = this;

    if (!node) return { node: undefined, ancestorIndex: undefined, ancestorParent: undefined };

    let current: NodeModel = node;

    while (current.parent) {
      if (current.parent.is(InlineNodeModel)) {
        return { node, ancestorIndex: current.index, ancestorParent: current.parent };
      }
      current = current.parent;
    }

    return { node, ancestorIndex: undefined, ancestorParent: undefined };
  };

  /**
   * Cursor recovery: when an ancestor of the cursor's node is removed from its parent inline,
   * (removeFromParent() sets the ancestor's index to -1 but doesn't clear the .parent reference),
   * move cursor to where that ancestor was in the parent inline.
   */
  private onAncestorInInlineChange = (
    { node, ancestorIndex }: ReturnType<typeof this.trackAncestorInInline>,
    prev: ReturnType<typeof this.trackAncestorInInline>,
  ) => {
    if (!node || node !== prev.node) {
      return;
    }

    const ancestorWasRemoved =
      ancestorIndex === -1 && prev?.ancestorIndex !== undefined && prev.ancestorIndex >= 0;
    const parentIsInline = prev?.ancestorParent?.is(InlineNodeModel);

    if (ancestorWasRemoved && parentIsInline) {
      this.moveTo(prev.ancestorParent, prev.ancestorIndex);
    }
  };

  dispose() {
    this.disposers.forEach((d) => d());
    this.disposers = [];
  }

  get selection() {
    return this.doc.selection;
  }

  get index(): number | undefined {
    if (!this.node?.nodes) {
      return undefined;
    }

    if (this.indexNodeId === undefined) {
      return 0;
    }

    if (this.indexNodeId === this.node.id) {
      return this.node.nodes.length;
    }

    const index = this.node.nodes.findIndex((n) => n.id === this.indexNodeId);
    return index === -1 ? Math.min(this.lastIndex || 0, this.node.nodes.length) : index + 1;
  }

  afterCursorMove(node: InlineNodeModel | undefined) {
    this.doc.editor.highlightFocusedDependencies();

    if (!node) return;

    const prevAncestors = node?.ancestors;
    const ancestors = this.node?.ancestors ?? [];
    const movedOutOfAncestors = difference(prevAncestors, ancestors);

    if (movedOutOfAncestors.length === 0) return;

    for (const n of movedOutOfAncestors) {
      if ("transform" in n) {
        n.transform();
      }
    }
  }

  get parentTiptapNode(): { node?: ProsemirrorNode; position: number } {
    const { tiptap } = this.doc.editor;
    const { parentWrapperNode } = this;
    if (!tiptap || !parentWrapperNode) {
      return {
        position: -1,
      };
    }
    let parentTiptapNode: ProsemirrorNode | undefined;
    let parentTiptapNodePosition: number | undefined = -1;

    tiptap.state.doc.descendants((node, position) => {
      if (nodeIsExpression(node) && node.attrs.nodeId === parentWrapperNode.id) {
        parentTiptapNode = node;
        parentTiptapNodePosition = position;
        return false;
      }
      return true;
    });

    return { node: parentTiptapNode, position: parentTiptapNodePosition };
  }

  get parentWrapperNode(): WrapperNodeModel | undefined {
    const parentWrapperNode = this.node?.ancestors.find((n): n is WrapperNodeModel =>
      n.is(WrapperNodeModel),
    );
    return parentWrapperNode;
  }

  private setTextCursor(node: NodeModel) {
    const wrapper = node?.ancestors.find((n) => n.is(WrapperNodeModel));

    const { tiptap } = this.doc.editor;

    if (!tiptap || !wrapper) {
      return;
    }

    const { from, to } = tiptap.state.selection;

    const pmNode = tiptap.state.doc.nodeAt(from);

    if (pmNode?.attrs.nodeId === wrapper.id) {
      if (to !== from + 1) {
        tiptap.commands.setNodeSelection(from);
      }
      return;
    }

    let nodeFound = false;

    tiptap.state.doc.descendants((n, position) => {
      if (nodeFound) return false;
      if (nodeIsExpression(n) && n.attrs.nodeId === wrapper.id) {
        tiptap.commands.setNodeSelection(position);
        nodeFound = true;
      }
      return true;
    });
  }

  private setIndexNodeId(index?: number) {
    if (index === undefined || index === 0) {
      this.indexNodeId = undefined;
    } else {
      this.indexNodeId = this.node?.nodes[index - 1]?.id;
    }
  }

  get nodeAfterCursor(): NodeModel | undefined {
    if (this.node && this.index !== undefined) {
      return this.node.nodes[this.index];
    }
    return undefined;
  }

  get nodeBeforeCursor(): NodeModel | undefined {
    if (this.node && this.index !== undefined && this.index > 0) {
      return this.node.nodes[this.index - 1];
    }
    return undefined;
  }

  get nodesBeforeCursor() {
    const { nodeBeforeCursor } = this;
    return nodeBeforeCursor ? [nodeBeforeCursor, ...nodeBeforeCursor.prevSiblings] : [];
  }

  get nodesAfterCursor() {
    const { nodeAfterCursor } = this;
    return nodeAfterCursor ? [nodeAfterCursor, ...nodeAfterCursor.nextSiblings] : [];
  }

  get movementType(): CursorMovementType {
    return this.movementTypeValue;
  }

  set movementType(movementType: CursorMovementType) {
    this.movementTypeValue = movementType;
  }

  private getNode(nodeOrId?: NodeModel | NodeId) {
    return typeof nodeOrId === "string" ? this.doc.nodes.get(nodeOrId) : nodeOrId;
  }

  isCurrent(nodeOrId: NodeModel | NodeId, index?: number): boolean {
    return computed(
      () => {
        const id = typeof nodeOrId === "string" ? nodeOrId : nodeOrId.id;
        return this.isActive && this.node?.id === id && this.index === index;
      },
      { name: "CursorModel.isCurrent" },
    ).get();
  }

  nodesAreFocused(parameters: NodeModel[]): boolean {
    return computed(() => !!this.node && parameters.includes(this.node), {
      name: "CursorModel.nodesAreFocused",
    }).get();
  }

  moveTo(
    nodeOrId?: NodeModel | NodeId,
    index?: number,
    options?: {
      /** For inline editable nodes this is the left offset in pixels to put the cursor. */
      left?: number;
      resetSelection?: boolean;
      /** Foce focus on input after the move */
      forceFocus?: boolean;
    },
  ): void {
    if (!nodeOrId) {
      this.reset();
    } else {
      const { left, resetSelection = true, forceFocus = false } = options ?? {};
      const targetNode = this.getNode(nodeOrId);
      if (!targetNode?.is(InlineNodeModel)) {
        console.warn("Trying to move cursor to a non-inline node", { node: targetNode });
        return;
      }

      const targetIndex = index ?? 0;
      const prevNode = this.node;
      const isNewPosition = targetNode !== this.node || targetIndex !== this.index;

      if (isNewPosition) {
        this.doc.editor.hints.setPhantomHints([]);
        this.node = targetNode;
        this.lastIndex = targetIndex;
        this.setIndexNodeId(targetIndex);
        this.setTextCursor(this.node);

        if (left && this.nodeAfterCursor?.isInlineEditable()) {
          this.doc.input.editNode(this.nodeAfterCursor, { left });
        }

        this.afterCursorMove(prevNode);
      } else {
        this.lastIndex = targetIndex;
        this.setIndexNodeId(targetIndex);

        // If your cursor in in math node
        // and you blur tiptap editor by clicking on sidebar for example
        // and then you try to move cursor back to the same math node
        // it won't let you
        // this code fixes it
        if (!this.doc.input.isFocused && !this.doc.editor.tiptap?.isFocused) {
          this.doc.input.focus();
        }
      }

      this.cursorHidden = false;

      if (resetSelection) {
        this.doc.selection.resetSelection(false);
      }
      if (forceFocus) {
        // focus/blur events create transaction with addToHistory=false
        // we need to make sure we are not breaking undo stack for the current transaction,
        // so the focus is delayed
        queueMicrotask(() => {
          this.doc.input.focus();
        });
      }
      this.releaseLockLeft();
    }
  }

  moveAfterNode(nodeOrId?: NodeModel | NodeId) {
    const node = this.getNode(nodeOrId);
    if (node && node.parent?.is(InlineNodeModel)) {
      this.moveTo(node.parent, node.index + 1);
    }
  }

  moveBeforeNode(nodeOrId?: NodeModel | NodeId, options?: { resetSelection?: boolean }) {
    const node = this.getNode(nodeOrId);
    if (node && node.parent?.is(InlineNodeModel)) {
      this.moveTo(node.parent, node.index, options);
    }
  }

  reset(): void {
    const prevNode = this.node;
    const wasActive = this.isActive;

    this.node = undefined;
    this.indexNodeId = undefined;

    if (wasActive) {
      this.afterCursorMove(prevNode);
    }
  }

  refreshTextCursor(): void {
    if (this.node) {
      this.setTextCursor(this.node);
    }
  }

  /**
   * Check if cursor is on the node or it's descendants.
   */
  isOnNode(node: NodeModel): boolean {
    return computed(
      () => {
        if (!this.node) {
          return false;
        }
        return !!(this.node === node || node.descendants.includes(this.node));
      },
      { name: "CursorModel.isOnNode" },
    ).get();
  }

  moveToWrapperStart(select = false) {
    if (this.node) {
      const wrapperNode = this.node.ancestors.find((n) => n.is(WrapperNodeModel));
      if (wrapperNode) {
        this.doc.selection.selectAfterCursor(() => {
          this.moveTo(wrapperNode.inlineNode, 0);
        }, !select);
      }
    }
  }

  moveToWrapperEnd(select = false) {
    if (this.node) {
      const wrapperNode = this.node.ancestors.find((n) => n.is(WrapperNodeModel));
      if (wrapperNode && wrapperNode.inlineNode) {
        this.doc.selection.selectAfterCursor(() => {
          this.moveTo(wrapperNode.inlineNode, wrapperNode.inlineNode?.nodes.length);
        }, !select);
      }
    }
  }

  moveToLineStart(select = false) {
    const { parentTiptapNode } = this;
    if (!parentTiptapNode?.node || !this.doc.editor.tiptap) return;

    const { tiptap } = this.doc.editor;
    const $start = tiptap.view.state.doc.resolve(parentTiptapNode.position);
    const startOfLinePos = $start.start($start.depth);

    if ($start.pos === startOfLinePos) {
      this.moveToWrapperStart(select);
    } else if (select) {
      // Select the part of the math to the left of the cursor, exactly like repeated
      // Shift+ArrowLeft: nothing when the cursor is at the math's left edge, the whole
      // node when at its right edge, a partial range in between. Setting the range up
      // front (rather than letting onSelectionUpdate auto-select the whole node) also
      // lets other math nodes on the line still get fully selected.
      const { node, index } = this;
      const atWrapperStart = this.isAtWrapperStart;
      const topInline = this.parentWrapperNode?.inlineNode;
      if (!atWrapperStart && topInline && node && index !== undefined) {
        this.doc.selection.setInlineNodeRangeBetween(node, index, topInline, 0);
      }
      // Anchor after the math node ($start.pos + 1) so the selected part stays covered by
      // the tiptap range; when nothing inside the math is selected, anchor before it.
      const from = atWrapperStart ? $start.pos : $start.pos + 1;
      this.reset();
      this.doc.input.reset(true);
      tiptap.chain().setTextSelection({ from, to: startOfLinePos }).focus().run();
    } else {
      jumpToSiblingNode("right", startOfLinePos, this.doc, tiptap);
    }
  }

  moveToLineEnd(select = false) {
    const { parentTiptapNode } = this;
    if (!parentTiptapNode?.node || !this.doc.editor.tiptap) return;

    const { tiptap } = this.doc.editor;
    const $start = tiptap.view.state.doc.resolve(parentTiptapNode.position);
    const endOfLinePos = $start.end($start.depth);

    if ($start.pos === endOfLinePos - 1) {
      this.moveToWrapperEnd(select);
    } else if (select) {
      // Select the part of the math to the right of the cursor, exactly like repeated
      // Shift+ArrowRight: nothing when the cursor is at the math's right edge, the whole
      // node when at its left edge, a partial range in between. Setting the range up
      // front (rather than letting onSelectionUpdate auto-select the whole node) also
      // lets other math nodes on the line still get fully selected.
      const { node, index } = this;
      const atWrapperEnd = this.isAtWrapperEnd;
      const topInline = this.parentWrapperNode?.inlineNode;
      if (!atWrapperEnd && topInline && node && index !== undefined) {
        this.doc.selection.setInlineNodeRangeBetween(
          node,
          index,
          topInline,
          topInline.nodes.length,
        );
      }
      // Anchor before the math node ($start.pos) so the selected part stays covered by the
      // tiptap range; when nothing inside the math is selected, anchor after it.
      const from = atWrapperEnd ? $start.pos + 1 : $start.pos;
      this.reset();
      this.doc.input.reset(true);
      tiptap
        .chain()
        .setTextSelection({ from, to: endOfLinePos + 1 })
        .focus()
        .run();
    } else {
      jumpToSiblingNode("left", endOfLinePos, this.doc, tiptap);
    }
  }

  moveToPrevSibling() {
    if (this.index === undefined || this.node === undefined) {
      return;
    }
    if (this.index > 0) {
      this.moveTo(this.node, this.index - 1);
    } else if (this.index === 0) {
      const prevInline = this.node.ancestors.find((n) => n.parent?.is(InlineNodeModel));
      if (prevInline) {
        this.moveTo(prevInline.parent, prevInline.index);
      } else {
        this.moveLeft();
      }
    }
  }

  moveToNextSibling() {
    if (this.index === undefined || this.node === undefined) {
      return;
    }
    if (this.index < this.node.nodes.length) {
      this.moveTo(this.node, this.index + 1);
    } else if (this.index === this.node.nodes.length) {
      const prevInline = this.node.ancestors.find((n) => n.parent?.is(InlineNodeModel));
      if (prevInline) {
        this.moveTo(prevInline.parent, prevInline.index + 1);
      } else {
        this.moveRight();
      }
    }
  }

  get isDefaultCursorContext(): boolean {
    return this.doc.cursorContextId === DEFAULT_CURSOR_CONTEXT_ID;
  }

  get isAtContextLeftBoundary(): boolean {
    const { boundaryInlineNodeIds } = this.doc.cursorContext(this.doc.cursorContextId) ?? {};

    if (boundaryInlineNodeIds) {
      return this.node?.id === boundaryInlineNodeIds[0] && this.index === 0;
    }

    return false;
  }

  get isAtContextRightBoundary(): boolean {
    const { boundaryInlineNodeIds } = this.doc.cursorContext(this.doc.cursorContextId) ?? {};

    if (boundaryInlineNodeIds) {
      const lastId = boundaryInlineNodeIds[boundaryInlineNodeIds.length - 1];
      return this.node?.id === lastId && this.index === this.node?.nodes.length;
    }

    return false;
  }

  /** True when the cursor sits at the leftmost edge of its enclosing math wrapper. */
  get isAtWrapperStart(): boolean {
    const { node, index, parentWrapperNode } = this;
    if (!node || index === undefined || !parentWrapperNode) return false;
    return findCursorPosition(node, index, "left") === undefined;
  }

  /** True when the cursor sits at the rightmost edge of its enclosing math wrapper. */
  get isAtWrapperEnd(): boolean {
    const { node, index, parentWrapperNode } = this;
    if (!node || index === undefined || !parentWrapperNode) return false;
    return findCursorPosition(node, index, "right") === undefined;
  }

  moveLeft() {
    this.moveLeftRight("left");
  }

  moveRight() {
    this.moveLeftRight("right");
  }

  moveLeftRight(direction: "left" | "right") {
    if (!this.isActive || !this.node || this.index === undefined) {
      return;
    }

    const isAtBoundary =
      direction === "left" ? this.isAtContextLeftBoundary : this.isAtContextRightBoundary;

    if (isAtBoundary) return;

    const target = findCursorPosition(this.node, this.index, direction);

    if (target) {
      this.moveTo(target.node, target.index);
    } else if (this.isDefaultCursorContext && this.parentWrapperNode) {
      this.moveToTextOrNextMathBlock(direction);
    }
  }

  moveToTextOrNextMathBlock(direction: "left" | "right") {
    const { tiptap } = this.doc.editor;
    const parentWrapperNode = this.node?.ancestors.find((n) =>
      n.is(WrapperNodeModel),
    ) as WrapperNodeModel;
    if (!tiptap || !parentWrapperNode) {
      return;
    }

    const { state } = tiptap;

    state.doc.descendants((node, position) => {
      if (nodeIsExpression(node) && node.attrs.nodeId === parentWrapperNode.id) {
        if (direction === "left") {
          const selection = Selection.findFrom(state.doc.resolve(position), -1);
          if (selection) {
            const prevNode = state.doc.nodeAt(selection.anchor);
            if (prevNode?.type.name === "image") {
              tiptap.chain().setNodeSelection(selection.anchor).focus().run();
            } else {
              tiptap.commands.focus(selection.from);
              this.reset();
            }
          } else {
            this.reset();
          }
        } else {
          const selection = Selection.findFrom(state.doc.resolve(position + 1), 1);
          if (selection) {
            const nextNode = state.doc.nodeAt(selection.anchor);
            if (nextNode?.type.name === "image") {
              tiptap.chain().setNodeSelection(selection.anchor).focus().run();
            } else {
              tiptap.commands.focus(selection.from);
              this.reset();
            }
          } else {
            this.reset();
          }
        }
        return false;
      }
      return true;
    });
  }

  moveUp(): boolean {
    return this.moveUpDown("up");
  }

  moveDown(): boolean {
    return this.moveUpDown("down");
  }

  private moveUpDown(direction: "up" | "down"): boolean {
    return this.getMoveToOptions(undefined, (options, lockedOptions) => {
      const moved = moveUpDown(this.doc, direction, options);
      if (moved) {
        return true;
      }
      return jumpLine(direction, lockedOptions, this.doc);
    });
  }

  jumpLine(direction: "up" | "down", opts?: MoveToOptions): boolean {
    return this.getMoveToOptions(opts, (_, lockedOptions) => {
      return jumpLine(direction, lockedOptions, this.doc);
    });
  }

  public getMoveToOptions(opts?: MoveToOptions): MoveToOptions | undefined;

  public getMoveToOptions(
    opts: MoveToOptions | undefined,
    callback: (opts: MoveToOptions, lockedOpts: MoveToOptions) => boolean,
  ): boolean;

  public getMoveToOptions(
    opts?: MoveToOptions,
    callback?: (opts: MoveToOptions, lockedOpts: MoveToOptions) => boolean,
  ): MoveToOptions | undefined | boolean {
    const active = document.activeElement;
    let options = opts;
    if ((!options && active instanceof HTMLInputElement) || active instanceof HTMLTextAreaElement) {
      const { top, bottom, left } = active.getBoundingClientRect();
      const caretLeft = getCaretCoordinates(active, active.selectionEnd ?? 0).left;

      const wrapper = active.closest(`.${WRAPPER_NODE_CLASS_NAME}`) as HTMLElement;

      this.doc.editor.resetInput(true);

      options = {
        left: left + caretLeft,
        top: (top + bottom) / 2,
        wrapper,
      };
    }
    if (options) {
      if (!this.lockLeft) {
        this.lockLeft = options.left;
      }
      const lockedOptions = {
        ...options,
        left: this.lockLeft,
      };

      if (callback) {
        this.allowReleaseLockLeft = false;
        const result = callback(options, lockedOptions);
        this.allowReleaseLockLeft = true;
        return result;
      }
      return options;
    }
    return undefined;
  }

  releaseLockLeft = () => {
    if (this.allowReleaseLockLeft && this.lockLeft) {
      this.lockLeft = undefined;
    }
  };

  moveToLevel(level = 0) {
    if (!this.isActive || !this.node || this.level === level) {
      return;
    }

    const ancestor = this.node.ancestors.find((node) => {
      if (node.parent?.is(InlineNodeModel)) {
        return getNodeLevel(node.parent) === level;
      }

      return false;
    });

    const moveToParams =
      (ancestor?.is(VariableNodeModel) || ancestor?.is(FunctionNodeModel)) &&
      ancestor.parameters[0];

    if (moveToParams) {
      const [param] = ancestor.parameters;
      const [firstNode] = param.nodes;

      if (firstNode?.is(PlaceholderNodeModel) && firstNode.inlineNode) {
        this.moveTo(firstNode.inlineNode, 0);
      } else {
        this.moveTo(ancestor.parameters[0], 0);
      }
    } else {
      this.moveAfterNode(ancestor);
    }
  }

  get level(): number {
    if (!this.node) return 0;
    return getNodeLevel(this.node);
  }

  get isActive() {
    return !!this.node;
  }

  get isAtTheEndOfExpression(): boolean {
    return !!(
      this.node &&
      this.node.parent?.is(WrapperNodeModel) &&
      this.index === this.node.nodes.length
    );
  }

  isAtTheEndOf<T extends NodeModel>(nodeType: NodeModel["type"] | ClassOf<T>): boolean {
    if (this.node && this.index !== undefined) {
      const isCorrectType =
        typeof nodeType === "string"
          ? this.node.parent?.type === nodeType
          : this.node.parent?.is(nodeType);

      return !!(isCorrectType && this.index === this.node.nodes.length);
    }

    return false;
  }

  cursorHidden = false;

  hideCursor() {
    this.cursorHidden = true;
  }
}

export interface ActiveCursorModel extends CursorModel {
  node: InlineNodeModel;
}

export function cursorIsActive(cursor: CursorModel): cursor is ActiveCursorModel {
  return cursor.isActive;
}
